import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { resolvePublicAssetUrl } from '../../../lib/assets'
import { formatFollowerGrowthPercent, pulseCardTooltipText } from '../../../lib/utils/pulseCardReport'
import KpiInfoTooltip from './KpiInfoTooltip'
import './KpiHoverLayer.css'

function pulseRowIconSrc(icon) {
  if (!icon) return null
  return resolvePublicAssetUrl(icon) || icon
}

function formatPlatformNetNew(value) {
  const n = Number(value) || 0
  if (n > 0) return `+${n.toLocaleString('en-US')}`
  if (n < 0) return `-${Math.abs(n).toLocaleString('en-US')}`
  return '0'
}

function KpiPlatformBreakdownList({
  rows,
  ariaLabel,
  totalColumnLabel,
  deltaColumnLabel = 'Net growth',
  getTotal,
  getDelta,
  showDelta = true,
  deltaFirst = false,
}) {
  if (!rows?.length) return null

  const listClassName = [
    'kpiHoverLayerPlatformList',
    showDelta ? '' : 'kpiHoverLayerPlatformList--noDelta',
    deltaFirst ? 'kpiHoverLayerPlatformList--deltaFirst' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={listClassName} aria-label={ariaLabel}>
      <div className="kpiHoverLayerPlatformHeader">
        <span>Platform</span>
        {deltaFirst && showDelta ? (
          <>
            <span>{deltaColumnLabel}</span>
            <span>{totalColumnLabel}</span>
          </>
        ) : (
          <>
            <span>{totalColumnLabel}</span>
            {showDelta ? <span>{deltaColumnLabel}</span> : null}
          </>
        )}
      </div>
      {rows.map((row) => {
        const delta = getDelta?.(row)
        const netClass =
          delta > 0
            ? 'liveNowCardDelta--up'
            : delta < 0
              ? 'liveNowCardDelta--down'
              : 'liveNowCardDelta--neutral'
        const totalCell = (
          <span
            className={`kpiHoverLayerPlatformTotal dashboard-tabular-nums${deltaFirst ? ' kpiHoverLayerPlatformTotal--secondary' : ''}`}
          >
            {Number(getTotal(row) || 0).toLocaleString('en-US')}
          </span>
        )
        const deltaCell = showDelta ? (
          <span className={`kpiHoverLayerPlatformNet dashboard-tabular-nums ${netClass}`}>
            {formatPlatformNetNew(delta)}
          </span>
        ) : null

        return (
          <div key={row.platform} className="kpiHoverLayerPlatformRow">
            <div className="kpiHoverLayerPlatformIdentity">
              {row.icon && (
                <img src={pulseRowIconSrc(row.icon)} alt="" className="kpiHoverLayerPlatformIcon" aria-hidden="true" />
              )}
              <span className="kpiHoverLayerPlatformName">{row.label}</span>
            </div>
            {deltaFirst && showDelta ? (
              <>
                {deltaCell}
                {totalCell}
              </>
            ) : (
              <>
                {totalCell}
                {deltaCell}
              </>
            )}
          </div>
        )
      })}
    </div>
  )
}

function GrowthPlatformFollowerList({ rows }) {
  if (!rows?.length) return null

  return (
    <div className="kpiHoverLayerPlatformList kpiHoverLayerPlatformList--pairedMetrics" aria-label="Followers by platform">
      <div className="kpiHoverLayerPlatformHeader">
        <span>Platform</span>
        <span className="kpiHoverLayerPlatformPairedHeader">
          <span>Net growth</span>
          <span>Followers</span>
        </span>
      </div>
      {rows.map((row) => {
        const growth = Number(row.growth)
        const growthRatio = Number.isFinite(growth) ? growth : 0
        const netClass =
          growthRatio > 0
            ? 'liveNowCardDelta--up'
            : growthRatio < 0
              ? 'liveNowCardDelta--down'
              : 'liveNowCardDelta--neutral'

        return (
          <div key={row.platform} className="kpiHoverLayerPlatformRow">
            <div className="kpiHoverLayerPlatformIdentity">
              {row.icon ? (
                <img src={pulseRowIconSrc(row.icon)} alt="" className="kpiHoverLayerPlatformIcon" aria-hidden="true" />
              ) : null}
              <span className="kpiHoverLayerPlatformName">{row.label}</span>
            </div>
            <div className="kpiHoverLayerPlatformPairedMetrics">
              <span className={`kpiHoverLayerPlatformNet dashboard-tabular-nums ${netClass}`}>
                {formatFollowerGrowthPercent(growthRatio)}
              </span>
              <span className="kpiHoverLayerPlatformTotal kpiHoverLayerPlatformTotal--secondary dashboard-tabular-nums">
                {Number(row.totalFollowers || 0).toLocaleString('en-US')}
              </span>
            </div>
          </div>
        )
      })}
    </div>
  )
}

function EngagementPlatformList({ rows }) {
  return (
    <KpiPlatformBreakdownList
      rows={rows}
      ariaLabel="Engagement by platform"
      totalColumnLabel="Engagement"
      getTotal={(row) => row.totalEngagements}
      showDelta={false}
    />
  )
}

function clampHoverLayerStyle(cardRect, overlayHeight) {
  const margin = 8
  const vw = typeof window !== 'undefined' ? window.innerWidth : 0
  const vh = typeof window !== 'undefined' ? window.innerHeight : 0
  const maxWidth = Math.max(0, vw - margin * 2)
  const width = Math.min(Math.max(Number(cardRect.width) || 0, 0), maxWidth)

  let left = Number(cardRect.left) || 0
  if (left + width > vw - margin) left = vw - margin - width
  if (left < margin) left = margin

  const maxHeight = Math.max(0, vh - margin * 2)
  const measured = overlayHeight || 180
  const height = Math.min(measured, maxHeight)
  let top = Number(cardRect.top) || 0
  if (top + height > vh - margin) top = vh - margin - height
  if (top < margin) top = margin

  return {
    position: 'fixed',
    left: `${left}px`,
    top: `${top}px`,
    width: `${width}px`,
    maxHeight: `${maxHeight}px`,
    zIndex: 110,
  }
}

function formatFollowerNewValueDelta(netNew) {
  const n = Number(netNew)
  const value = Number.isFinite(n) ? n : 0
  const arrow = value >= 0 ? '\u25B2' : '\u25BC'
  const abs = Math.abs(value)
  const formatted = abs >= 1000 ? `${(abs / 1000).toFixed(1)}K` : String(abs)
  return `${arrow} ${formatted}`
}

function growthDeltaTrendClass(netGrowth) {
  const n = Number(netGrowth)
  if (n > 0) return 'liveNowCardDelta--up'
  if (n < 0) return 'liveNowCardDelta--down'
  return 'liveNowCardDelta--neutral'
}

function getGrowthNetClass(card) {
  const netGrowth = Number(card.netGrowthToday) || 0
  if (netGrowth > 0) return 'liveNowCardDelta--up'
  if (netGrowth < 0) return 'liveNowCardDelta--down'
  return 'liveNowCardDelta--neutral'
}

function renderGrowthSecondaryMetric(card) {
  if (!card.secondaryValue) return null
  return (
    <span className="liveNowCardNetGrowth">
      <span className="liveNowCardNetGrowthLabel">Net growth</span>
      <span className={`kpiHoverLayerDelta liveNowCardSecondaryMetric ${getGrowthNetClass(card)}`}>
        {card.secondaryValue}
      </span>
    </span>
  )
}

function renderGrowthMetric(card, deltaText, effectiveClass) {
  return (
    <div className="kpiHoverLayerMetric">
      <div className="kpiHoverLayerValueRow">
        <span className="kpiHoverLayerValueMain dashboard-tabular-nums">
          {card.value}
        </span>
        <span className={`kpiHoverLayerDelta ${effectiveClass}`}>
          {deltaText}
        </span>
      </div>
      {renderGrowthSecondaryMetric(card)}
    </div>
  )
}

const KpiHoverLayer = ({
  hoveredCard,
  cardRect,
  renderPlatformIcons,
  onMouseEnter,
  onMouseLeave,
  overlayRef: forwardedOverlayRef,
  id: overlayId,
}) => {
  const internalOverlayRef = useRef(null)
  const setOverlayRef = (el) => {
    internalOverlayRef.current = el
    if (forwardedOverlayRef) forwardedOverlayRef.current = el
  }
  const [overlayHeight, setOverlayHeight] = useState(180)

  useEffect(() => {
    if (internalOverlayRef.current && hoveredCard) {
      requestAnimationFrame(() => {
        if (internalOverlayRef.current) {
          const height = internalOverlayRef.current.offsetHeight || 180
          setOverlayHeight(height)
        }
      })
    }
  }, [
    hoveredCard,
    hoveredCard?.platformFollowerRows?.length,
    hoveredCard?.platformEngagementRows?.length,
    cardRect?.width,
    cardRect?.height,
  ])

  if (!hoveredCard || !cardRect) return null

  const style = clampHoverLayerStyle(cardRect, overlayHeight)

  const COUNT_METRIC_IDS = ['inbox', 'campaign', 'growth']
  const isCountMetric = (id) => COUNT_METRIC_IDS.includes(id)

  const data = hoveredCard.sparklineData || []
  const hasData = data.length >= 2
  const trend = hoveredCard.sparklineTrend || 'stable'
  const trendClass = trend === 'up' ? 'liveNowCardDelta--up' : trend === 'down' ? 'liveNowCardDelta--down' : 'liveNowCardDelta--neutral'
  const isGrowthCard = hoveredCard.id === 'growth'

  let deltaText
  let effectiveClass
  if (isGrowthCard) {
    deltaText = formatFollowerNewValueDelta(hoveredCard.followerNewValue)
    effectiveClass = growthDeltaTrendClass(hoveredCard.followerNewValue)
  } else if (hasData) {
    if (isCountMetric(hoveredCard.id)) {
      const first = data[0] || 0
      const last = data[data.length - 1] || 0
      const absDelta = last - first
      const arrow = absDelta >= 0 ? '\u25B2' : '\u25BC'
      const formatted = Math.abs(absDelta) >= 1000 ? `${(Math.abs(absDelta) / 1000).toFixed(1)}K` : String(Math.abs(absDelta))
      deltaText = `${arrow} ${formatted}`
    } else {
      const pct = hoveredCard.sparklinePercentChange
      deltaText = pct != null ? `${pct >= 0 ? '\u25B2' : '\u25BC'} ${Math.abs(pct).toFixed(1)}%` : '\u2014'
    }
    effectiveClass = trendClass
  } else {
    deltaText = '\u2014'
    effectiveClass = 'liveNowCardDelta--neutral'
  }

  const overlay = (
    <div
      ref={setOverlayRef}
      id={overlayId}
      className="kpiHoverLayer kpiHoverLayer--visible"
      style={style}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="kpiHoverLayerContent">
        <div className="kpiHoverLayerHeader">
          <div className="kpiHoverLayerTitleRow">
            <div className="kpiHoverLayerTitleWithInfo">
              <div className="kpiHoverLayerTitle">{hoveredCard.title}</div>
              <KpiInfoTooltip
                content={pulseCardTooltipText(hoveredCard)}
                aria-label={`${hoveredCard.title} definition`}
                portaled
                placement="bottom"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              />
            </div>
          </div>
          {renderPlatformIcons && (
            <div className="kpiHoverLayerTrendMeta">
              {renderPlatformIcons(hoveredCard)}
            </div>
          )}
        </div>
        
        {hoveredCard.id === 'growth' && hoveredCard.secondaryValue
          ? renderGrowthMetric(hoveredCard, deltaText, effectiveClass)
          : (
            <div className="kpiHoverLayerMetric">
              <div className="kpiHoverLayerValueRow">
                <span className="kpiHoverLayerValueMain dashboard-tabular-nums">{hoveredCard.value}</span>
                <span className={`kpiHoverLayerDelta ${effectiveClass}`}>{deltaText}</span>
              </div>
              {hoveredCard.subLabel && (
                <span className="kpiHoverLayerValueSuffix">{hoveredCard.subLabel}</span>
              )}
            </div>
          )}

        {hoveredCard.trophyInsight && (
          <>
            <div className="kpiHoverLayerDivider" />
            <div className="kpiHoverLayerSummary">{hoveredCard.trophyInsight}</div>
          </>
        )}

        {hoveredCard.id === 'growth' && hoveredCard.platformFollowerRows?.length > 0 ? (
          <GrowthPlatformFollowerList rows={hoveredCard.platformFollowerRows} />
        ) : hoveredCard.id === 'engagement' && hoveredCard.platformEngagementRows?.length > 0 ? (
          <EngagementPlatformList rows={hoveredCard.platformEngagementRows} />
        ) : hoveredCard.detailRows && hoveredCard.detailRows.length > 0 ? (
          <div className="kpiHoverLayerDetails">
            {hoveredCard.detailRows.map((row, index) => (
              <div key={index} className="kpiHoverLayerDetailRow">
                <span className="kpiHoverLayerDetailLabel">{row.label}</span>
                <span className="kpiHoverLayerDetailValue">{row.value}</span>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  )

  return createPortal(overlay, document.body)
}

export default KpiHoverLayer
